import { lazy, Suspense, useEffect, useMemo, type JSX } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";
import { Toaster } from "sonner";

import { LoadingState, RouteFallback } from "./components/data/index.js";
import { AppShell } from "./components/shell/AppShell.js";
import { CompanyGate } from "./components/shell/CompanyGate.js";
import { RouteErrorBoundary } from "./components/shell/RouteErrorBoundary.js";
import { RouteGuard } from "./components/shell/RouteGuard.js";
import { isPublicPath, stripTenantPrefix } from "./lib/tenant-url.js";
import { AuthProvider, useAuth } from "./providers/auth-provider.js";
import { QueryProvider } from "./providers/query-provider.js";
import { SelectedStoreProvider } from "./providers/selected-store-provider.js";
import { ThemeProvider } from "./providers/theme-provider.js";
import { AcceptInvitePage } from "./routes/accept-invite.js";
import { DashboardPage } from "./routes/dashboard.js";
import { ForgotPasswordPage } from "./routes/forgot-password.js";
import { LoginPage } from "./routes/login.js";
import OnboardingPage from "./routes/onboarding.js";
import { ResetPasswordPage } from "./routes/reset-password.js";
import SelectCompanyPage from "./routes/select-company.js";
import { AccountPage } from "./routes/settings/account.js";
import { SessionsPage } from "./routes/settings/sessions.js";
import { UsersPage } from "./routes/settings/users.js";
import { SignupPage } from "./routes/signup.js";
import { VerifyEmailPage } from "./routes/verify-email.js";

const StoreLayoutHubPage = lazy(() => import("./routes/store-layout/index.js"));
const StoreLayoutMapsPage = lazy(() => import("./routes/store-layout/maps.js"));
const StoreLayoutActiveMapPage = lazy(() => import("./routes/store-layout/map.js"));
const StoreLayoutThreePage = lazy(() => import("./routes/store-layout/three.js"));
const FixturePlanogramPage = lazy(() => import("./routes/store-layout/fixture-planogram.js"));
const FixturesListPage = lazy(() => import("./routes/store-layout/fixtures.js"));
const FixturesBulkCategoriesPage = lazy(
  () => import("./routes/store-layout/fixtures-bulk-categories.js"),
);
const FixtureCreatePage = lazy(() => import("./routes/store-layout/fixtures-create.js"));
const FixtureEditPage = lazy(() => import("./routes/store-layout/fixtures-edit.js"));
const ShelvesListPage = lazy(() => import("./routes/store-layout/shelves.js"));
const ShelfCreatePage = lazy(() => import("./routes/store-layout/shelves-create.js"));
const ShelfEditPage = lazy(() => import("./routes/store-layout/shelves-edit.js"));
const ZonesListPage = lazy(() => import("./routes/store-layout/zones.js"));
const ZoneCreatePage = lazy(() => import("./routes/store-layout/zones-create.js"));
const ZoneEditPage = lazy(() => import("./routes/store-layout/zones-edit.js"));
const ProductsListPage = lazy(() => import("./routes/products/index.js"));
const ProductCreatePage = lazy(() => import("./routes/products/create.js"));
const ProductsOnboardPage = lazy(() => import("./routes/products/onboard.js"));
const ProductImportPage = lazy(() => import("./routes/products/import.js"));
const ProductExportPage = lazy(() => import("./routes/products/export.js"));
const ProductCategoriesPage = lazy(() => import("./routes/products/categories.js"));
const ProductDetailPage = lazy(() => import("./routes/products/detail.js"));
const ProductEditPage = lazy(() => import("./routes/products/edit.js"));
const PlanogramsListPage = lazy(() => import("./routes/planograms/index.js"));
const PlanogramCreatePage = lazy(() => import("./routes/planograms/create.js"));
const PlanogramDetailPage = lazy(() => import("./routes/planograms/detail.js"));
const PlanogramEditPage = lazy(() => import("./routes/planograms/edit.js"));
const PlanogramAssignmentsListPage = lazy(() => import("./routes/planogram-assignments/index.js"));
const PlanogramAssignmentCreatePage = lazy(
  () => import("./routes/planogram-assignments/create.js"),
);
const ScansListPage = lazy(() => import("./routes/scans/index.js"));
const NewScanPage = lazy(() => import("./routes/scans/new.js"));
const ScanDetailPage = lazy(() => import("./routes/scans/detail.js"));
const CaptureAnalysisPage = lazy(() => import("./routes/scans/capture-analysis.js"));
const TaskAnalyticsPage = lazy(() => import("./routes/scans/task-analytics.js"));
const VisionRunDetailPage = lazy(() => import("./routes/scans/vision-run-detail.js"));
const ScanReportPage = lazy(() => import("./routes/scans/report.js"));
const ComplianceIssuesPage = lazy(() => import("./routes/compliance/index.js"));
const ComplianceStoreTasksPage = lazy(() => import("./routes/compliance/store-tasks.js"));
const StoreTaskDetailPage = lazy(() => import("./routes/compliance/store-task-detail.js"));
const IssueDetailPage = lazy(() => import("./routes/compliance/detail.js"));
const BrandAnalyticsPage = lazy(() => import("./routes/analytics/brands.js"));
const CategoryAnalyticsPage = lazy(() => import("./routes/analytics/categories.js"));
const LocationAnalyticsPage = lazy(() => import("./routes/analytics/locations.js"));
const StoreMapAnalyticsPage = lazy(() => import("./routes/analytics/map.js"));
const LegacyCatalogAnalyticsRedirect = lazy(() => import("./routes/analytics/catalog.js"));
const BrandsPage = lazy(() => import("./routes/products/brands.js"));
const AnalyticsPage = lazy(() => import("./routes/analytics/index.js"));
const AnalyticsTrendsPage = lazy(() => import("./routes/analytics/trends.js"));
const AuditTrailPage = lazy(() => import("./routes/audit/index.js"));
const CompanySettingsPage = lazy(() => import("./routes/settings/company.js"));
const WebhooksPage = lazy(() => import("./routes/settings/webhooks.js"));
const WebhookCreatePage = lazy(() => import("./routes/settings/webhooks-create.js"));
const WebhookDetailPage = lazy(() => import("./routes/settings/webhooks-detail.js"));
const ApiKeysPage = lazy(() => import("./routes/settings/api-keys.js"));
const DevicesPage = lazy(() => import("./routes/settings/devices.js"));
const DeviceCreatePage = lazy(() => import("./routes/settings/devices-create.js"));
const DeviceEditPage = lazy(() => import("./routes/settings/devices-edit.js"));
const StoresPage = lazy(() => import("./routes/settings/stores.js"));
const StoreCreatePage = lazy(() => import("./routes/settings/stores-create.js"));
const StoreEditPage = lazy(() => import("./routes/settings/stores-edit.js"));
const StoreCategoriesPage = lazy(() => import("./routes/settings/stores-categories.js"));

// Public + auth + company routes — root paths, NO tenant slug. Their flow is
// intentionally untouched by the tenant-slug change.
const publicRouter = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/signup", element: <SignupPage /> },
  { path: "/verify-email", element: <VerifyEmailPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  // Path matches the server-built email link (invitations.service.ts:
  // ACCEPT_LINK_PATH = "accept-invitation"). Don't rename without
  // updating that constant too — they're a contract.
  { path: "/accept-invitation", element: <AcceptInvitePage /> },
  {
    // Authenticated but OUTSIDE the AppShell + CompanyGate — these are the
    // escape hatches the gate redirects to, so they must not be gated.
    path: "/select-company",
    element: (
      <RouteGuard>
        <SelectCompanyPage />
      </RouteGuard>
    ),
  },
  {
    path: "/onboarding",
    element: (
      <RouteGuard>
        <OnboardingPage />
      </RouteGuard>
    ),
  },
  // Unknown root path while on the public router → login.
  { path: "*", element: <Navigate to="/login" replace /> },
]);

// The authenticated app. Mounted by RootRouter under a per-tenant basename
// `/t/<code>`, so every existing absolute link (`to="/scans"`) resolves under
// the slug automatically — no per-link changes. The gates stay here; they
// redirect via full navigation (window.location) so they escape the basename
// back to the root auth/company pages.
const appRoutes = [
  {
    element: (
      <RouteGuard>
        <CompanyGate>
          <SelectedStoreProvider>
            <AppShell />
          </SelectedStoreProvider>
        </CompanyGate>
      </RouteGuard>
    ),
    children: [
      {
        // Pathless wrapper: page errors bubble here and render the branded
        // boundary inside the AppShell outlet, so the sidebar/topbar survive.
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: "/",
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: "/dashboard",
            element: <DashboardPage />,
            handle: { crumb: "Dashboard" },
          },
          {
            path: "/settings/account",
            element: <AccountPage />,
            handle: { crumb: "Account" },
          },
          {
            path: "/settings/sessions",
            element: <SessionsPage />,
            handle: { crumb: "Sessions" },
          },
          {
            path: "/settings/users",
            element: <UsersPage />,
            handle: { crumb: "Team" },
          },
          {
            path: "/settings/webhooks",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <WebhooksPage />
              </Suspense>
            ),
            handle: { crumb: "Webhooks" },
          },
          {
            path: "/settings/api-keys",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ApiKeysPage />
              </Suspense>
            ),
            handle: { crumb: "API keys" },
          },
          {
            path: "/settings/webhooks/new",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <WebhookCreatePage />
              </Suspense>
            ),
            handle: {
              crumb: "New webhook",
              parent: { label: "Webhooks", to: "/settings/webhooks" },
            },
          },
          {
            path: "/settings/webhooks/:publicId",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <WebhookDetailPage />
              </Suspense>
            ),
            handle: {
              crumb: "Webhook",
              parent: { label: "Webhooks", to: "/settings/webhooks" },
            },
          },
          {
            path: "/settings/devices",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <DevicesPage />
              </Suspense>
            ),
            handle: { crumb: "Devices" },
          },
          {
            path: "/settings/devices/create",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <DeviceCreatePage />
              </Suspense>
            ),
            handle: {
              crumb: "New device",
              parent: { label: "Devices", to: "/settings/devices" },
            },
          },
          {
            path: "/settings/devices/:publicId/edit",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <DeviceEditPage />
              </Suspense>
            ),
            handle: {
              crumb: "Edit device",
              parent: { label: "Devices", to: "/settings/devices" },
            },
          },
          {
            path: "/settings/stores",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <StoresPage />
              </Suspense>
            ),
            handle: { crumb: "Stores" },
          },
          {
            path: "/settings/stores/create",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <StoreCreatePage />
              </Suspense>
            ),
            handle: {
              crumb: "New store",
              parent: { label: "Stores", to: "/settings/stores" },
            },
          },
          {
            path: "/settings/stores/:publicId/edit",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <StoreEditPage />
              </Suspense>
            ),
            handle: {
              crumb: "Edit store",
              parent: { label: "Stores", to: "/settings/stores" },
            },
          },
          {
            path: "/settings/stores/:publicId/categories",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <StoreCategoriesPage />
              </Suspense>
            ),
            handle: {
              crumb: "Store categories",
              parent: { label: "Stores", to: "/settings/stores" },
            },
          },
          {
            path: "/store-layout",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <StoreLayoutHubPage />
              </Suspense>
            ),
            handle: { crumb: "Store Layout" },
          },
          {
            path: "/store-layout/maps",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <StoreLayoutMapsPage />
              </Suspense>
            ),
            handle: {
              crumb: "Maps",
              parent: { label: "Store Layout", to: "/store-layout" },
            },
          },
          {
            path: "/store-layout/map",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <StoreLayoutActiveMapPage />
              </Suspense>
            ),
            handle: {
              crumb: "Active Map",
              parent: { label: "Store Layout", to: "/store-layout" },
            },
          },
          {
            path: "/store-layout/3d",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <StoreLayoutThreePage />
              </Suspense>
            ),
            handle: {
              crumb: "3D store",
              parent: { label: "Store Layout", to: "/store-layout" },
            },
          },
          {
            path: "/store-layout/fixtures",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <FixturesListPage />
              </Suspense>
            ),
            handle: { crumb: "Fixtures" },
          },
          {
            path: "/store-layout/fixtures/:publicId/planogram",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <FixturePlanogramPage />
              </Suspense>
            ),
            handle: {
              crumb: "Planogram",
              parent: { label: "Fixtures", to: "/store-layout/fixtures" },
            },
          },
          {
            path: "/store-layout/fixtures/categories",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <FixturesBulkCategoriesPage />
              </Suspense>
            ),
            handle: {
              crumb: "Bulk Category Mapping",
              parent: { label: "Fixtures", to: "/store-layout/fixtures" },
            },
          },
          {
            path: "/store-layout/fixtures/create",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <FixtureCreatePage />
              </Suspense>
            ),
            handle: {
              crumb: "New Fixture",
              parent: { label: "Fixtures", to: "/store-layout/fixtures" },
            },
          },
          {
            path: "/store-layout/fixtures/:publicId/edit",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <FixtureEditPage />
              </Suspense>
            ),
            handle: {
              crumb: "Edit Fixture",
              parent: { label: "Fixtures", to: "/store-layout/fixtures" },
            },
          },
          {
            path: "/store-layout/zones",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ZonesListPage />
              </Suspense>
            ),
            handle: { crumb: "Zones" },
          },
          {
            path: "/store-layout/zones/create",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ZoneCreatePage />
              </Suspense>
            ),
            handle: {
              crumb: "New Zone",
              parent: { label: "Zones", to: "/store-layout/zones" },
            },
          },
          {
            path: "/store-layout/zones/:publicId/edit",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ZoneEditPage />
              </Suspense>
            ),
            handle: {
              crumb: "Edit Zone",
              parent: { label: "Zones", to: "/store-layout/zones" },
            },
          },
          {
            path: "/store-layout/shelves",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ShelvesListPage />
              </Suspense>
            ),
            handle: { crumb: "Shelves" },
          },
          {
            path: "/store-layout/shelves/create",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ShelfCreatePage />
              </Suspense>
            ),
            handle: {
              crumb: "New Shelf",
              parent: { label: "Shelves", to: "/store-layout/shelves" },
            },
          },
          {
            path: "/store-layout/shelves/:publicId/edit",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ShelfEditPage />
              </Suspense>
            ),
            handle: {
              crumb: "Edit Shelf",
              parent: { label: "Shelves", to: "/store-layout/shelves" },
            },
          },
          {
            path: "/products",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ProductsListPage />
              </Suspense>
            ),
            handle: { crumb: "Products" },
          },
          {
            path: "/products/create",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ProductCreatePage />
              </Suspense>
            ),
            handle: {
              crumb: "New Product",
              parent: { label: "Products", to: "/products" },
            },
          },
          {
            path: "/products/onboard",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ProductsOnboardPage />
              </Suspense>
            ),
            handle: {
              crumb: "Onboard",
              parent: { label: "Products", to: "/products" },
            },
          },
          {
            path: "/products/import",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ProductImportPage />
              </Suspense>
            ),
            handle: {
              crumb: "Import",
              parent: { label: "Products", to: "/products" },
            },
          },
          {
            path: "/products/export",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ProductExportPage />
              </Suspense>
            ),
            handle: {
              crumb: "Export",
              parent: { label: "Products", to: "/products" },
            },
          },
          {
            path: "/products/categories",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ProductCategoriesPage />
              </Suspense>
            ),
            handle: {
              crumb: "Categories",
              parent: { label: "Products", to: "/products" },
            },
          },
          {
            path: "/products/:publicId",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ProductDetailPage />
              </Suspense>
            ),
            handle: {
              crumb: "Product",
              parent: { label: "Products", to: "/products" },
            },
          },
          {
            path: "/products/:publicId/edit",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ProductEditPage />
              </Suspense>
            ),
            handle: {
              crumb: "Edit Product",
              parent: { label: "Products", to: "/products" },
            },
          },
          {
            path: "/planograms",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <PlanogramsListPage />
              </Suspense>
            ),
            handle: { crumb: "Planograms" },
          },
          {
            path: "/planograms/create",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <PlanogramCreatePage />
              </Suspense>
            ),
            handle: {
              crumb: "New planogram",
              parent: { label: "Planograms", to: "/planograms" },
            },
          },
          {
            path: "/planograms/:publicId",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <PlanogramDetailPage />
              </Suspense>
            ),
            handle: {
              crumb: "Planogram",
              parent: { label: "Planograms", to: "/planograms" },
            },
          },
          {
            path: "/planograms/:publicId/edit",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <PlanogramEditPage />
              </Suspense>
            ),
            handle: {
              crumb: "Edit planogram",
              parent: { label: "Planograms", to: "/planograms" },
            },
          },
          {
            path: "/planogram-assignments",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <PlanogramAssignmentsListPage />
              </Suspense>
            ),
            handle: {
              crumb: "Assignments",
              parent: { label: "Planograms", to: "/planograms" },
            },
          },
          {
            path: "/planogram-assignments/create",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <PlanogramAssignmentCreatePage />
              </Suspense>
            ),
            handle: {
              crumb: "New assignment",
              parent: { label: "Assignments", to: "/planogram-assignments" },
            },
          },
          {
            path: "/scans",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ScansListPage />
              </Suspense>
            ),
            handle: { crumb: "Scans" },
          },
          {
            path: "/scans/new",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <NewScanPage />
              </Suspense>
            ),
            handle: {
              crumb: "New Scan",
              parent: { label: "Scans", to: "/scans" },
            },
          },
          {
            path: "/scans/captures/:captureId/analysis",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <CaptureAnalysisPage />
              </Suspense>
            ),
            handle: {
              crumb: "Capture analysis",
              parent: { label: "Scans", to: "/scans" },
            },
          },
          {
            path: "/scans/tasks/:taskId/analytics",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <TaskAnalyticsPage />
              </Suspense>
            ),
            handle: {
              crumb: "Task analytics",
              parent: { label: "Scans", to: "/scans" },
            },
          },
          {
            path: "/scans/vision-runs/:runId",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <VisionRunDetailPage />
              </Suspense>
            ),
            handle: {
              crumb: "Vision run",
              parent: { label: "Scans", to: "/scans" },
            },
          },
          {
            path: "/scans/:publicId/report",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ScanReportPage />
              </Suspense>
            ),
            handle: {
              crumb: "Report",
              parent: { label: "Scans", to: "/scans" },
            },
          },
          {
            path: "/scans/:publicId",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ScanDetailPage />
              </Suspense>
            ),
            handle: {
              crumb: "Scan",
              parent: { label: "Scans", to: "/scans" },
            },
          },
          {
            path: "/compliance",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ComplianceIssuesPage />
              </Suspense>
            ),
            handle: { crumb: "Issues" },
          },
          {
            path: "/compliance/store-tasks",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ComplianceStoreTasksPage />
              </Suspense>
            ),
            handle: {
              crumb: "Store tasks",
              parent: { label: "Issues", to: "/compliance" },
            },
          },
          {
            path: "/compliance/store-tasks/:taskId",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <StoreTaskDetailPage />
              </Suspense>
            ),
          },
          {
            path: "/compliance/:publicId",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <IssueDetailPage />
              </Suspense>
            ),
            handle: {
              crumb: "Issue",
              parent: { label: "Issues", to: "/compliance" },
            },
          },
          {
            path: "/analytics",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <AnalyticsPage />
              </Suspense>
            ),
            handle: { crumb: "Analytics" },
          },
          {
            path: "/analytics/catalog",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <LegacyCatalogAnalyticsRedirect />
              </Suspense>
            ),
            handle: { crumb: "Analytics", parent: { label: "Analytics", to: "/analytics" } },
          },
          {
            path: "/analytics/brands",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <BrandAnalyticsPage />
              </Suspense>
            ),
            handle: { crumb: "Brand analytics", parent: { label: "Analytics", to: "/analytics" } },
          },
          {
            path: "/analytics/categories",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <CategoryAnalyticsPage />
              </Suspense>
            ),
            handle: {
              crumb: "Category analytics",
              parent: { label: "Analytics", to: "/analytics" },
            },
          },
          {
            path: "/analytics/locations",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <LocationAnalyticsPage />
              </Suspense>
            ),
            handle: {
              crumb: "Location analytics",
              parent: { label: "Analytics", to: "/analytics" },
            },
          },
          {
            path: "/analytics/map",
            element: <Suspense fallback={<RouteFallback />}><StoreMapAnalyticsPage /></Suspense>,
            handle: {crumb: "Store map analytics", parent: {label: "Analytics", to: "/analytics"}},
          },
          {
            path: "/products/brands",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <BrandsPage />
              </Suspense>
            ),
            handle: { crumb: "Brands", parent: { label: "Products", to: "/products" } },
          },
          {
            path: "/analytics/trends",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <AnalyticsTrendsPage />
              </Suspense>
            ),
            handle: { crumb: "Trends", parent: { label: "Analytics", to: "/analytics" } },
          },
          {
            path: "/audit",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <AuditTrailPage />
              </Suspense>
            ),
            handle: { crumb: "Audit Trail" },
          },
          {
            path: "/settings/company",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <CompanySettingsPage />
              </Suspense>
            ),
            handle: { crumb: "Company" },
          },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/dashboard" replace /> },
];

function makeAppRouter(basename: string): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(appRoutes, { basename });
}

/**
 * Root resolver — picks which router to mount:
 *  - auth/company pages (root paths) → public router (flow unchanged)
 *  - authed + active tenant on an app URL → app router (basename `/t/<code>`)
 *  - missing/mismatched `/t/<slug>` on an app URL → rewritten to the active
 *    tenant's slug (preserving the sub-path) via a full navigation
 *  - otherwise (unauthed/tenant-less on an app URL) → public router → /login
 *
 * The app router is keyed on the tenant code, so a tenant switch re-mounts a
 * fresh router with the new basename.
 */
function RootRouter(): JSX.Element {
  const { user, loading } = useAuth();
  const tenantCode = user?.tenantCode ?? null;
  const appRouter = useMemo(
    () => (tenantCode ? makeAppRouter(`/t/${tenantCode}`) : null),
    [tenantCode],
  );

  const path = window.location.pathname;
  const onPublic = isPublicPath(path);
  const useApp = !loading && !!user && !!tenantCode && !!appRouter && !onPublic;
  const sub = stripTenantPrefix(path);
  const desired = tenantCode ? `/t/${tenantCode}${sub === "/" ? "/dashboard" : sub}` : null;
  const needsRedirect = useApp && desired !== null && path !== desired;

  useEffect(() => {
    if (needsRedirect && desired) {
      window.location.replace(desired + window.location.search);
    }
  }, [needsRedirect, desired]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingState variant="spinner" message="Checking session…" />
      </div>
    );
  }
  if (onPublic || !useApp) return <RouterProvider router={publicRouter} />;
  if (needsRedirect || !appRouter) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingState variant="spinner" message="Loading workspace…" />
      </div>
    );
  }
  return <RouterProvider router={appRouter} />;
}

export function App(): JSX.Element {
  return (
    <ThemeProvider>
      <QueryProvider>
        <AuthProvider>
          <RootRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </QueryProvider>
    </ThemeProvider>
  );
}
